import React, { useState, useEffect, useCallback, useRef } from 'react';
import { auth } from '../../lib/firebase';
import { getAuth } from 'firebase/auth';
import {
  FileText, Plus, Search, X, Edit3, Trash2, Copy, Clock, ChevronDown, ChevronUp,
  Bold, Italic, Underline, List, AlignLeft, AlignCenter, Link, Eye, Code, RefreshCw,
  Tag, Save, AlertCircle, CheckCircle, RotateCcw, ChevronLeft, Paperclip, Send,
  Smartphone, Monitor, Sparkles, Sliders, Layout, Upload, File, HelpCircle, Check,
  LayoutGrid, Table as TableIcon,
} from 'lucide-react';
import Modal from '../shared/Modal';
import DocumentTemplateMultiSelect from '../shared/DocumentTemplateMultiSelect';
import SearchableSelect, { SearchableOption } from '../shared/SearchableSelect';
import ImproveTextDropdown from '../shared/ImproveTextDropdown';
import MergeFieldPicker from '../shared/MergeFieldPicker';
import { parseMergeTag, formatCaseValue } from '../shared/CaseFormatSelector';
// Shared email composer — replaces the hand-rolled execCommand editor
// (and its unsanitized innerHTML reads) with the one canonical stack.
import { useComposerBody } from './email/useComposerBody';
import { ComposerBody } from './email/ComposerBody';
import { MERGE_FIELD_CATEGORIES } from './email/mergeFieldCategories';
import { sanitizeEmailHtml } from './email/emailSanitize';
import { sanitizeTemplateRecord, searchText } from '../../lib/piiEnvelope';
import { confirmDialog } from '../../lib/confirmStore';
import { notifyToast } from '../../lib/toastStore';

async function getIdToken(): Promise<string> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

interface TemplateAttachment {
  name: string;
  base64Content: string;
  mimeType: string;
}

interface TemplateVersion {
  version: number;
  subject: string;
  htmlBody: string;
  attachments?: TemplateAttachment[];
  documentTemplateIds?: string[];
  savedAt: string;
  revertedFrom?: number;
}

interface Template {
  id: string;
  templateId: string;
  code?: string;
  name: string;
  tags: string[];
  subject: string;
  htmlBody: string;
  attachments?: TemplateAttachment[];
  documentTemplateIds?: string[];
  version: number;
  versions?: TemplateVersion[];
  versionCount?: number;
  hasAttachments?: boolean;
  createdAt: string;
  updatedAt: string;
}

/* ── Sample Applicant Data for Preview and Test Emails ── */
const SAMPLE_APPLICANT = {
  personalDetails: {
    firstName: 'Jane',
    lastName: 'Doe',
    phone: '+1-555-0192',
    dob: '1996-08-24',
    countryOfCitizenship: 'United States',
    address: { street: '742 Evergreen Terrace', city: 'Springfield', state: 'IL', zip: '62701', country: 'USA' },
  },
  userEmail: 'jane.doe@example.com',
  applicationId: 'APP-98241',
  status: 'Submitted',
  submissionStatus: 'Submitted',
  masterStage: 3,
  pipelineStage: 3,
  activeStatus: true,
  programName: 'J-1 Internship',
  batchName: 'Fall 2025 Cohort',
  createdAt: new Date().toISOString(),
  placementStatus: 'Confirmed',
  assignment: {
    employerName: 'Global Horizon Tech',
    city: 'San Francisco',
    state: 'CA',
    startDate: '2025-09-01',
    endDate: '2026-03-01',
    programName: 'J-1 Internship',
  },
  visaDetails: {
    visaNumber: 'V-8829102',
    visaType: 'J-1',
    issueDate: '2025-08-01',
    expirationDate: '2026-08-01',
    conditions: 'No restrictions',
    ds160ConfirmationNumber: 'DS160-99120',
    embassyAppointmentDate: '2025-08-15T10:00:00',
    embassyLocation: 'US Embassy London',
  },
  ds160Details: { ds160Number: 'DS160-99120', submissionStatus: 'Submitted' },
  embassyDetails: { appointmentDate: '2025-08-15T10:00:00', location: 'US Embassy London', interviewResult: 'Approved' },
  events: [
    { name: 'Pre-Departure Orientation Session', date: new Date(Date.now() + 86400000 * 5).toISOString(), location: 'Virtual Jitsi Room' },
  ],
};

/* ── Tag resolver ── */
const resolveSimpleTag = (app: any, tag: string): string => {
  if (!app) return '{{' + tag + '}}';
  const map: Record<string, (a: any) => string> = {
    first_name: a => a.personalDetails?.firstName || 'N/A',
    last_name: a => a.personalDetails?.lastName || 'N/A',
    full_name: a => [a.personalDetails?.firstName, a.personalDetails?.lastName].filter(Boolean).join(' ') || a.userEmail || 'N/A',
    email: a => a.userEmail || 'N/A',
    phone: a => a.personalDetails?.phone || a.phone || 'N/A',
    date_of_birth: a => a.personalDetails?.dob || 'N/A',
    nationality: a => a.personalDetails?.countryOfCitizenship || 'N/A',
    address: a => { const ad = a.personalDetails?.address; if (!ad) return 'N/A'; return [ad.street, ad.city, ad.state, ad.zip, ad.country].filter(Boolean).join(', ') || 'N/A'; },
    application_id: a => a.applicationId || 'N/A',
    application_status: a => a.status || a.submissionStatus || 'N/A',
    master_phase: a => { const s = a.masterStage ?? a.pipelineStage; return s != null ? String(s) : 'N/A'; },
    active_status: a => a.activeStatus !== false && a.activeStatus !== undefined ? 'Active' : 'Inactive',
    program_name: a => a.assignment?.programName || a.programName || a.programType || 'N/A',
    batch_name: a => a.batchName || 'N/A',
    application_created_date: a => a.createdAt ? new Date(a.createdAt).toLocaleDateString() : 'N/A',
    portal_link: a => a.applicationId ? `${window.__FRONTEND_URL || 'https://portal.isats.org'}/applicant/${a.applicationId}` : 'N/A',
    placement_status: a => a.placementStatus || 'Pending',
    placement_employer: a => a.assignment?.employerName || a.serviceProvider?.name || 'N/A',
    placement_location: a => [a.assignment?.city, a.assignment?.state].filter(Boolean).join(', ') || 'N/A',
    placement_start_date: a => a.assignment?.startDate || 'N/A',
    placement_end_date: a => a.assignment?.endDate || 'N/A',
    visa_number: a => a.visaDetails?.visaNumber || a.passportImmigration?.visaNumber || 'N/A',
    visa_type: a => a.visaDetails?.visaType || a.passportImmigration?.visaType || 'N/A',
    visa_issue_date: a => a.visaDetails?.issueDate || 'N/A',
    visa_expiry_date: a => a.visaDetails?.expirationDate || 'N/A',
    visa_conditions: a => a.visaDetails?.conditions || a.visaDetails?.annotations || 'N/A',
    ds160_number: a => a.ds160Details?.ds160Number || a.visaDetails?.ds160ConfirmationNumber || 'N/A',
    ds160_status: a => a.ds160Details?.submissionStatus || 'N/A',
    embassy_appointment_datetime: a => a.embassyDetails?.appointmentDate || a.visaDetails?.embassyAppointmentDate || 'N/A',
    embassy_appointment_location: a => a.embassyDetails?.location || a.visaDetails?.embassyLocation || 'N/A',
    embassy_interview_result: a => a.embassyDetails?.interviewResult || 'N/A',
    next_event_name: a => { const evts = a.events || []; const up = evts.filter((e: any) => e.date && new Date(e.date) > new Date()).sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()); return up[0]?.name || 'N/A'; },
    next_event_date: a => { const evts = a.events || []; const up = evts.filter((e: any) => e.date && new Date(e.date) > new Date()).sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()); return up[0]?.date ? new Date(up[0].date).toLocaleString() : 'N/A'; },
    next_event_location: a => { const evts = a.events || []; const up = evts.filter((e: any) => e.date && new Date(e.date) > new Date()).sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()); return up[0]?.location || up[0]?.link || 'N/A'; },
  };
  const fn = map[tag];
  if (fn) return fn(app);
  const parts = tag.split('.');
  let val: any = app;
  for (const p of parts) { if (val == null) return 'N/A'; if (/^\d+$/.test(p) && Array.isArray(val)) { val = val[parseInt(p)] || {}; continue; } val = val[p]; }
  return val != null ? String(val) : 'N/A';
};

const resolveText = (text: string, sampleApp?: any): string => {
  if (!sampleApp) return text;
  return text.replace(/\{\{(\S+?)\}\}/g, (_m: string, tag: string) => {
    const { fieldName, format } = parseMergeTag(`{{${tag}}}`);
    const raw = resolveSimpleTag(sampleApp, fieldName);
    return formatCaseValue(raw, format);
  });
};

/* ── Tag input component ── */
function TagInput({ tags, onChange, availableTags = [] }: { tags: string[]; onChange: (t: string[]) => void; availableTags?: string[] }) {
  const [input, setInput] = useState('');

  const addTag = (tagToAdd?: string) => {
    const trimmed = (tagToAdd || input).trim();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
      if (!tagToAdd) setInput('');
    }
  };

  const defaultPresets = ['System', 'Onboarding', 'Visa', 'Screening', 'Placement', 'Reminder', 'Welcome', 'Billing'];
  const allAvailable = Array.from(new Set([...availableTags, ...defaultPresets])).filter(t => !tags.includes(t));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5 p-2 bg-slate-50 border border-slate-200 focus-within:border-brand focus-within:bg-white rounded-xl transition-all min-h-[42px]">
        {tags.map((tag, i) => (
          <span key={i} className="flex items-center gap-1 px-2.5 py-1 bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-bold rounded-lg shadow-2xs">
            <span>{tag}</span>
            <button aria-label="Remove tag" title="Remove tag" type="button" onClick={() => onChange(tags.filter((_, j) => j !== i))} className="text-indigo-400 hover:text-rose-600 transition-colors cursor-pointer"><X className="h-3 w-3" /></button>
          </span>
        ))}
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
          placeholder={tags.length === 0 ? 'Type tag and press Enter...' : 'Add tag...'}
          className="flex-1 min-w-[120px] text-xs border-0 outline-none bg-transparent p-0.5 text-slate-800 placeholder:text-slate-400"
        />
      </div>

      {allAvailable.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          <span className="text-[10px] uppercase font-extrabold text-slate-400">Available Active Tags:</span>
          {allAvailable.slice(0, 10).map(tag => (
            <button
              key={tag}
              type="button"
              onClick={() => addTag(tag)}
              className="px-2 py-0.5 bg-slate-100 hover:bg-indigo-100 text-slate-600 hover:text-indigo-700 text-[11px] font-semibold rounded-md transition-colors cursor-pointer"
            >
              + {tag}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Main Email Template Manager Component ── */
interface EmailTemplateManagerProps {
  canEdit?: boolean;
}

export default function EmailTemplateManager({ canEdit = true }: EmailTemplateManagerProps = {}) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [search, setSearch] = useState('');
  const [filterTag, setFilterTag] = useState<string | null>(null);
  const [filterScope, setFilterScope] = useState<'all' | 'mine' | 'shared'>('all');
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Sharing State (default false for new drafts so unfinished work is not shared prematurely)
  const [isShared, setIsShared] = useState(false);

  // Editor State
  const [name, setName] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  // Body lives in the shared composer (canonical HTML state); `htmlBody`
  // stays as a read-only alias so preview/detection code is unchanged.
  const templateComposer = useComposerBody({});
  const htmlBody = templateComposer.bodyHtml;
  const [attachments, setAttachments] = useState<TemplateAttachment[]>([]);
  // Document templates pre-selected on this email template: wherever the
  // template is used, these are generated per recipient at send time and
  // attached (resolved server-side from the send's documentTemplateIds).
  const [documentTemplateIds, setDocumentTemplateIds] = useState<string[]>([]);
  const [previewTab, setPreviewTab] = useState<'editor' | 'preview' | 'split'>('editor');
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');

  // Test Email State
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testStatus, setTestStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  // Version History Panel State
  const [showVersions, setShowVersions] = useState(false);

  // Applicant Mail Merge Context State
  const [applicantList, setApplicantList] = useState<{ id: string; name: string; email: string; appId: string; appData: any }[]>([]);
  const [applicantSearch, setApplicantSearch] = useState('');
  const [selectedApplicantId, setSelectedApplicantId] = useState('');
  const [selectedApplicantData, setSelectedApplicantData] = useState<any>(SAMPLE_APPLICANT);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch Applicants for Mail Merge
  useEffect(() => {
    (async () => {
      try {
        const token = await getIdToken();
        const res = await fetch('/api/admin/applicant-profiles', { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (res.ok && Array.isArray(data.applications)) {
          const list = data.applications.map((app: any) => {
            const pd = app.personalDetails || {};
            const name = [pd.firstName, pd.lastName].filter(Boolean).join(' ') || app.userEmail || app.applicationId || 'Applicant';
            return {
              id: app.applicationId || app.id,
              name,
              email: app.userEmail || pd.email || '',
              appId: app.applicationId || app.id,
              appData: app
            };
          });
          setApplicantList(list);
        }
      } catch (err) {
        console.warn('Mail merge applicants load error:', err);
      }
    })();
  }, []);

  const handleSelectApplicant = (appId: string) => {
    setSelectedApplicantId(appId);
    if (!appId) {
      setSelectedApplicantData(SAMPLE_APPLICANT);
      setApplicantSearch('');
      return;
    }
    const found = applicantList.find(a => a.id === appId);
    if (found) {
      setSelectedApplicantData(found.appData);
      setApplicantSearch(found.name);
    }
  };

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getIdToken();
      const res = await fetch('/api/email-templates', { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      // Same ingest guard as TemplatePickerModal (piiEnvelope): keep
      // non-string stored fields (numeric codes, envelope objects)
      // out of state — renders and the search filter assume strings.
      if (res.ok) setTemplates((data.templates || []).map((t: any) => sanitizeTemplateRecord(t)));
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const allTags = React.useMemo(() => {
    const s = new Set<string>();
    templates.forEach(t => (t.tags || []).forEach(tag => s.add(tag)));
    return Array.from(s).sort();
  }, [templates]);

  const isTemplateOwner = useCallback((t: Template) => {
    const currentUser = auth.currentUser || getAuth().currentUser;
    if (!currentUser) return false;
    const uid = currentUser.uid;
    const email = (currentUser.email || '').toLowerCase();
    const createdBy = (t as any).createdBy;
    const creatorEmail = ((t as any).creatorEmail || '').toLowerCase();
    const adminId = (t as any).adminId;

    if (createdBy && createdBy === uid) return true;
    if (adminId && adminId === uid) return true;
    if (creatorEmail && creatorEmail === email) return true;
    if (!createdBy && !adminId && !creatorEmail) return true; // Legacy template
    return false;
  }, []);

  const filtered = React.useMemo(() => {
    let r = templates;
    const currentUser = auth.currentUser || getAuth().currentUser;
    const currentUid = currentUser?.uid || '';
    const currentEmail = (currentUser?.email || '').toLowerCase();

    // Filter templates per user: creators see private or shared templates; team members see shared or own templates
    r = r.filter(t => {
      if ((t as any).isShared !== false) return true; // Shared with workspace
      const creator = (t as any).createdBy || (t as any).adminId;
      const creatorEmail = ((t as any).creatorEmail || '').toLowerCase();
      if (creator && (creator === currentUid || creatorEmail === currentEmail)) return true; // Own private template
      if (!creator && !creatorEmail) return true; // Legacy template
      return false;
    });

    if (filterScope === 'mine') {
      r = r.filter(t => isTemplateOwner(t));
    } else if (filterScope === 'shared') {
      r = r.filter(t => (t as any).isShared !== false);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter(t => searchText(t.name).toLowerCase().includes(q) || searchText(t.subject).toLowerCase().includes(q) || searchText(t.code).toLowerCase().includes(q) || (t.tags || []).some(tag => searchText(tag).toLowerCase().includes(q)));
    }
    if (filterTag) r = r.filter(t => (t.tags || []).includes(filterTag));
    return r;
  }, [templates, search, filterTag, filterScope, isTemplateOwner]);

  const handleAttachment = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { notifyToast('warning', 'Attachment file size must be under 5MB'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      setAttachments(prev => [...prev, { name: file.name, base64Content: base64, mimeType: file.type }]);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const resetForm = async () => {
    if ((name || subject || htmlBody) && !(await confirmDialog('Discard unsaved template changes?'))) return;
    setName(''); setTags([]); setSubject(''); templateComposer.reset(''); setAttachments([]); setDocumentTemplateIds([]);
    setIsShared(true); setEditingTemplate(null); setIsCreating(false);
    setSaveError(''); setSaveSuccess(''); setShowVersions(false); setPreviewTab('editor');
  };

  const startEdit = (t: Template) => {
    setEditingTemplate(t);
    setIsCreating(false);
    setName(t.name);
    setTags(t.tags || []);
    setSubject(t.subject);
    templateComposer.reset(t.htmlBody || '');
    setAttachments((t as any).attachments || []);
    setDocumentTemplateIds(Array.isArray((t as any).documentTemplateIds) ? (t as any).documentTemplateIds : []);
    setIsShared((t as any).isShared !== false);
    setPreviewTab('editor');
    setSaveError('');
    setSaveSuccess('');
    setShowVersions(false);
  };

  const startCreate = async () => {
    await resetForm();
    setIsCreating(true);
  };

  const startDuplicate = async (t: Template) => {
    await resetForm();
    setIsCreating(true);
    setName(t.name + ' (Copy)');
    setTags(t.tags || []);
    setSubject(t.subject);
    templateComposer.reset(t.htmlBody || '');
    setAttachments((t as any).attachments || []);
    setDocumentTemplateIds(Array.isArray((t as any).documentTemplateIds) ? (t as any).documentTemplateIds : []);
  };

  const handleSave = async () => {
    if (!name.trim() || !subject.trim()) { setSaveError('Template name and subject are required.'); return; }
    // Save the canonical send pipeline output (sanitized + inlined) —
    // previously the raw contentEditable innerHTML was stored unsanitized.
    const finalBody = templateComposer.getSendHtml();
    if (templateComposer.isEmpty || !finalBody.trim()) { setSaveError('Email body content cannot be empty.'); return; }

    setSaving(true); setSaveError(''); setSaveSuccess('');
    try {
      const token = await getIdToken();
      const body = { name: name.trim(), tags, subject: subject.trim(), htmlBody: finalBody, attachments, documentTemplateIds, isShared };
      let res: Response;
      if (editingTemplate) {
        res = await fetch(`/api/email-templates/${editingTemplate.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
      } else {
        res = await fetch('/api/email-templates', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setSaveSuccess(editingTemplate ? `Template updated (v${data.version})` : 'Template created');
      setTimeout(() => setSaveSuccess(''), 3500);
      await resetForm();
      await fetchTemplates();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const token = await getIdToken();
      const res = await fetch(`/api/email-templates/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      setDeleteConfirm(null);
      await fetchTemplates();
    } catch (err) {
      notifyToast('error', 'Delete failed: ' + (err instanceof Error ? err.message : String(err)));
    }
  };

  const handleRevert = async (version: number) => {
    if (!editingTemplate) return;
    try {
      const token = await getIdToken();
      const res = await fetch(`/api/email-templates/${editingTemplate.id}/revert`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ version }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Revert failed');
      await fetchTemplates();
      const r2 = await fetch(`/api/email-templates/${editingTemplate.id}`, { headers: { Authorization: `Bearer ${token}` } });
      const d2 = await r2.json();
      if (r2.ok && d2.template) {
        startEdit(d2.template);
        setSaveSuccess(`Reverted to version ${version}`);
        setTimeout(() => setSaveSuccess(''), 3000);
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Revert failed');
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailAddress.trim()) {
      setTestStatus({ type: 'error', msg: 'Please enter a valid target email address.' });
      return;
    }
    setTestSending(true);
    setTestStatus(null);
    try {
      const token = await getIdToken();
      const currentBody = templateComposer.bodyHtml;
      const activeApp = selectedApplicantData || SAMPLE_APPLICANT;
      const res = await fetch('/api/email/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          to: testEmailAddress.trim(),
          subject: resolveText(subject, activeApp),
          htmlBody: resolveText(currentBody, activeApp),
          applicantData: activeApp,
          attachments,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send test email');
      setTestStatus({ type: 'success', msg: `Test email successfully sent to ${testEmailAddress}` });
    } catch (err) {
      setTestStatus({ type: 'error', msg: err instanceof Error ? err.message : 'Failed to send test email' });
    } finally {
      setTestSending(false);
    }
  };

  const previewContent = React.useMemo(() => {
    // Canonical composer state — the old code read the contentEditable's
    // innerHTML during render, which could lag or miss the latest edit.
    const activeApp = selectedApplicantData || SAMPLE_APPLICANT;
    return {
      subject: resolveText(subject, activeApp),
      body: resolveText(templateComposer.getSendHtml(), activeApp),
    };
  }, [subject, templateComposer, selectedApplicantData]);

  // Extract variables referenced in subject + body
  const detectedVariables = React.useMemo(() => {
    const fullText = subject + ' ' + htmlBody;
    const matches = fullText.match(/\{\{(\S+?)\}\}/g) || [];
    const unique = Array.from(new Set(matches.map(m => m.replace(/[\{\}]/g, ''))));
    return unique;
  }, [subject, htmlBody]);

  if (isCreating || editingTemplate) {
    // ── Editor / Studio View ──
    return (
      <div className="space-y-4 animate-fade-in">
        {/* Top Header Studio Action Bar */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs flex flex-wrap items-center justify-between gap-3 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button onClick={resetForm} className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-all cursor-pointer flex items-center gap-1.5 text-xs font-bold">
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Templates</span>
            </button>
            <div className="h-4 w-px bg-slate-200" />
            <div className="min-w-0">
              <h3 className="text-sm font-extrabold text-slate-900 truncate">
                {name || (editingTemplate ? editingTemplate.name : 'Untitled Email Template')}
              </h3>
              <p className="text-caption text-slate-400 font-medium">
                {editingTemplate ? `Version ${editingTemplate.version} · Last updated ${new Date(editingTemplate.updatedAt).toLocaleDateString()}` : 'New Template Draft'}
              </p>
            </div>
          </div>

          {/* Mode Toggles & Action Buttons */}
          <div className="flex items-center gap-2">
            {/* View Mode Switcher */}
            <div className="flex items-center bg-slate-100 p-0.5 rounded-xl">
              <button
                onClick={() => setPreviewTab('editor')}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                  previewTab === 'editor' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Edit3 className="h-3.5 w-3.5" />
                <span className="hidden md:inline">Edit</span>
              </button>
              <button
                onClick={() => setPreviewTab('split')}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                  previewTab === 'split' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Layout className="h-3.5 w-3.5" />
                <span className="hidden md:inline">Split</span>
              </button>
              <button
                onClick={() => setPreviewTab('preview')}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                  previewTab === 'preview' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Eye className="h-3.5 w-3.5" />
                <span className="hidden md:inline">Preview</span>
              </button>
            </div>

            {/* Share with Workspace toggle */}
            <label className="px-3 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={isShared}
                onChange={e => setIsShared(e.target.checked)}
                className="rounded text-indigo-600 focus:ring-brand-primary h-3.5 w-3.5"
              />
              <span className="hidden sm:inline">Share with Team</span>
            </label>

            {/* Test Email Button */}
            <button
              onClick={() => setShowTestModal(true)}
              className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1.5"
              title="Send a live test email"
            >
              <Send className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Test Email</span>
            </button>

            {/* Versions button */}
            {editingTemplate && (
              <button
                onClick={() => setShowVersions(!showVersions)}
                className={`px-3 py-1.5 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1 border ${
                  showVersions ? 'bg-brand text-white border-brand' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                }`}
              >
                <Clock className="h-3.5 w-3.5" />
                <span className="hidden md:inline">v{editingTemplate.version}</span>
                {showVersions ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>
            )}

            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-1.5 btn-primary text-white text-xs font-bold rounded-xl transition-all cursor-pointer disabled:opacity-50 flex items-center gap-1.5 shadow-2xs"
            >
              {saving ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              <span>{saving ? 'Saving...' : editingTemplate ? 'Update' : 'Save Template'}</span>
            </button>
          </div>
        </div>

        {/* Feedback banners */}
        {saveError && (
          <div className="flex items-center gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 px-4 py-2.5 rounded-xl">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{saveError}</span>
          </div>
        )}
        {saveSuccess && (
          <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-4 py-2.5 rounded-xl">
            <CheckCircle className="h-4 w-4 shrink-0" />
            <span>{saveSuccess}</span>
          </div>
        )}

        {/* Version History Drawer */}
        {showVersions && editingTemplate && (
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden animate-fade-in">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-indigo-600" />
                <h4 className="text-xs font-extrabold text-slate-800">Version History (Last 5 saved versions)</h4>
              </div>
              <button aria-label="Close" title="Close" onClick={() => setShowVersions(false)} className="text-slate-400 hover:text-slate-700 cursor-pointer"><X className="h-4 w-4" /></button>
            </div>
            <div className="divide-y divide-slate-100 max-h-52 overflow-y-auto">
              {(editingTemplate.versions || []).slice().reverse().map((v: any) => (
                <div key={v.version} className="flex items-center justify-between px-5 py-3 text-xs hover:bg-slate-50 transition-colors">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <span className="font-extrabold text-slate-800">Version {v.version}</span>
                      {v.version === editingTemplate.version && (
                        <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded">Current</span>
                      )}
                      {v.revertedFrom && <span className="text-amber-600 font-semibold text-[10px]">(reverted from v{v.revertedFrom})</span>}
                    </div>
                    <p className="text-caption text-slate-400">{new Date(v.savedAt).toLocaleString()} · Subject: "{v.subject}"</p>
                  </div>
                  <button onClick={() => handleRevert(v.version)} className="px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1">
                    <RotateCcw className="h-3 w-3" />
                    <span>Revert</span>
                  </button>
                </div>
              ))}
              {(!editingTemplate.versions || editingTemplate.versions.length === 0) && (
                <div className="px-5 py-6 text-xs text-slate-500 text-center">No previous version history available.</div>
              )}
            </div>
          </div>
        )}

        {/* Main Workspace Grid (Editor / Split / Preview) */}
        <div className={`grid gap-5 ${previewTab === 'split' ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
          {/* EDITOR FORM PANEL */}
          {(previewTab === 'editor' || previewTab === 'split') && (
            <div className="bg-white border border-slate-200 rounded-2xl shadow-xs p-5 space-y-4">
              {/* Template Name & Tags */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-caption uppercase font-extrabold text-slate-500 block mb-1">
                    Template Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="e.g. Welcome & Onboarding Overview"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 outline-none focus:bg-white focus:border-brand transition-all"
                  />
                </div>
                <div>
                  <label className="text-caption uppercase font-extrabold text-slate-500 block mb-1">Tags / Category</label>
                  <TagInput tags={tags} onChange={setTags} availableTags={allTags} />
                </div>
              </div>

              {/* Subject Line */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-caption uppercase font-extrabold text-slate-500">
                    Subject Line <span className="text-rose-500">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-slate-400">{subject.length} chars</span>
                    <MergeFieldPicker
                      categories={MERGE_FIELD_CATEGORIES}
                      onInsert={(tag) => setSubject(prev => prev + tag)}
                      buttonLabel="Insert Field"
                      align="right"
                    />
                  </div>
                </div>
                <input
                  type="text"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  placeholder="e.g. Welcome to {{program_name}}, {{first_name}}! (ID: {{application_id}})"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 outline-none focus:bg-white focus:border-brand transition-all font-medium"
                />
              </div>

              {/* Email Content Body — shared WYSIWYG composer */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-caption uppercase font-extrabold text-slate-500">
                    Email Body Content <span className="text-rose-500">*</span>
                  </label>
                </div>
                <ComposerBody
                  composer={templateComposer}
                  minHeight={260}
                  placeholder="Compose your email template content here..."
                  fieldPickerSlot={
                    <MergeFieldPicker
                      categories={MERGE_FIELD_CATEGORIES}
                      onInsert={(tag) => templateComposer.insertTextAtCursor(tag)}
                      buttonLabel="Insert Field"
                      align="right"
                    />
                  }
                  toolbarExtraSlot={
                    <ImproveTextDropdown
                      getValue={() => templateComposer.bodyHtml}
                      onImprove={(improved) => templateComposer.setBodyHtml(improved)}
                      isHtml={true}
                      context="email template body"
                      subject={subject}
                    />
                  }
                />
              </div>

              {/* Attachments Section */}
              <div className="border-t border-slate-100 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <Paperclip className="h-4 w-4 text-indigo-600" />
                    <span className="text-caption uppercase font-extrabold text-slate-600">Attached Files ({attachments.length})</span>
                  </div>
                  <label className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all cursor-pointer">
                    <Upload className="h-3.5 w-3.5" />
                    <span>Upload Attachment</span>
                    <input ref={fileInputRef} type="file" className="hidden" onChange={handleAttachment} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.xlsx,.pptx,.txt,.csv" />
                  </label>
                </div>

                {attachments.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {attachments.map((a, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 bg-indigo-50/60 border border-indigo-100 rounded-xl text-xs font-bold text-indigo-900">
                        <div className="flex items-center gap-2 min-w-0">
                          <File className="h-4 w-4 text-indigo-600 shrink-0" />
                          <span className="truncate max-w-[160px]">{a.name}</span>
                        </div>
                        <button aria-label="Remove attachment" title="Remove attachment" type="button" onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))} className="text-indigo-400 hover:text-rose-600 cursor-pointer p-1">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-caption text-slate-400 italic">No attachments added yet (PDF, DOCX, images up to 5MB supported).</p>
                )}
              </div>

              {/* Generated Document Templates Section — pre-selected on this
                  email template; resolved automatically wherever the template
                  is used (compose / batch / automation) and adjustable at
                  compose time. */}
              <div className="border-t border-slate-100 pt-3">
                <DocumentTemplateMultiSelect
                  value={documentTemplateIds}
                  onChange={setDocumentTemplateIds}
                  label="Generated Documents"
                  hint="Pre-selected for this template. When an email using this template is sent, each document is generated from the recipient's application, saved to their Generated Documents, and attached."
                />
              </div>

              {/* Detected Merge Variables Summary */}
              {detectedVariables.length > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Tag className="h-3.5 w-3.5 text-indigo-600" />
                    <span className="text-caption font-bold text-slate-600 uppercase">Detected Variables ({detectedVariables.length})</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {detectedVariables.map(v => (
                      <span key={v} className="px-2 py-0.5 bg-white border border-slate-200 text-slate-700 font-mono text-[10px] font-bold rounded-md">
                        {`{{${v}}}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* PREVIEW PANEL */}
          {(previewTab === 'preview' || previewTab === 'split') && (
            <div className="bg-white border border-slate-200 rounded-2xl shadow-xs p-5 space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4 text-indigo-600" />
                  <h4 className="text-xs font-extrabold text-slate-800">Live Rendered Preview</h4>
                </div>

                {/* Device Frame Toggles */}
                <div className="flex items-center bg-slate-100 p-0.5 rounded-lg">
                  <button
                    onClick={() => setPreviewDevice('desktop')}
                    className={`p-1.5 rounded-md transition-all cursor-pointer ${
                      previewDevice === 'desktop' ? 'bg-white text-slate-800 shadow-2xs' : 'text-slate-400 hover:text-slate-700'
                    }`}
                    title="Desktop Preview"
                  >
                    <Monitor className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setPreviewDevice('mobile')}
                    className={`p-1.5 rounded-md transition-all cursor-pointer ${
                      previewDevice === 'mobile' ? 'bg-white text-slate-800 shadow-2xs' : 'text-slate-400 hover:text-slate-700'
                    }`}
                    title="Mobile Preview"
                  >
                    <Smartphone className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {/* Sample Data & Mail Merge Selector */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 px-3 py-2 bg-indigo-50/60 border border-indigo-200 rounded-xl text-xs text-indigo-900">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-indigo-600 shrink-0" />
                  <span>Mail Merge Preview Context:</span>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-80">
                  <div className="w-full">
                    <SearchableSelect
                      value={selectedApplicantId}
                      onChange={(val) => handleSelectApplicant(val)}
                      options={[
                        { value: '', label: `Sample Candidate (${SAMPLE_APPLICANT.personalDetails.firstName} ${SAMPLE_APPLICANT.personalDetails.lastName})`, hint: 'Default Preview' },
                        ...applicantList.map((a) => ({
                          value: a.id,
                          label: a.name,
                          hint: a.appId,
                          tag: a.programName || undefined
                        }))
                      ]}
                      placeholder="Search recipient candidate..."
                      allowClear={false}
                    />
                  </div>
                </div>
              </div>

              {/* Email Envelope Container */}
              <div className={`mx-auto transition-all ${previewDevice === 'mobile' ? 'max-w-[340px]' : 'w-full'}`}>
                <div className="bg-slate-50 border border-slate-200 rounded-2xl overflow-hidden shadow-xs">
                  {/* Fake Email Header */}
                  <div className="bg-white border-b border-slate-200 p-3.5 space-y-1.5">
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span className="font-semibold">From: <strong className="text-slate-800">ISATS Portal Support &lt;noreply@isats.org&gt;</strong></span>
                      <span className="text-[10px]">Just now</span>
                    </div>
                    <div className="text-xs text-slate-500">
                      <span>To: <strong className="text-slate-800">{SAMPLE_APPLICANT.personalDetails.firstName} {SAMPLE_APPLICANT.personalDetails.lastName} &lt;{SAMPLE_APPLICANT.userEmail}&gt;</strong></span>
                    </div>
                    <div className="pt-1.5 border-t border-slate-100">
                      <p className="text-xs font-bold text-slate-900">
                        Subject: <span className="font-semibold text-slate-800">{previewContent.subject || '(No subject specified)'}</span>
                      </p>
                    </div>
                  </div>

                  {/* Rendered HTML Body */}
                  <div className="p-5 bg-white min-h-[320px] text-xs text-slate-800 leading-relaxed overflow-y-auto">
                    <div
                      dangerouslySetInnerHTML={{
                        // Shared email sanitize config (previewContent.body
                        // is already the composer's sanitized send output;
                        // this is the same-config second pass for safety).
                        __html: sanitizeEmailHtml(previewContent.body || '<p class="text-slate-400 italic">No email body content entered.</p>'),
                      }}
                    />
                  </div>

                  {/* Attached Files Footer in Preview */}
                  {attachments.length > 0 && (
                    <div className="bg-slate-50 border-t border-slate-200 p-3 flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-bold text-slate-400 uppercase">Attachments ({attachments.length}):</span>
                      {attachments.map((a, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-slate-200 rounded text-[10px] font-bold text-slate-700">
                          <Paperclip className="h-3 w-3 text-indigo-500" />
                          <span>{a.name}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Test Email Modal */}
        {showTestModal && (
          <Modal onClose={() => setShowTestModal(false)}>
            <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4 space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <Send className="h-5 w-5 text-indigo-600" />
                  <h3 className="text-sm font-extrabold text-slate-900">Send Test Email</h3>
                </div>
                <button aria-label="Close" title="Close" onClick={() => setShowTestModal(false)} className="text-slate-400 hover:text-slate-600 cursor-pointer">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <p className="text-xs text-slate-600 leading-relaxed">
                Send a live email to test variables rendering and visual formatting using sample applicant data.
              </p>

              {documentTemplateIds.length > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 leading-relaxed">
                  This template pre-selects {documentTemplateIds.length} generated document{documentTemplateIds.length === 1 ? '' : 's'}. Test emails use sample data and never generate documents, so they are not attached here — documents are generated from the recipient's application only when the template is really sent to an applicant.
                </p>
              )}

              {testStatus && (
                <div className={`p-3 rounded-xl text-xs flex items-center gap-2 ${
                  testStatus.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-rose-50 text-rose-800 border border-rose-200'
                }`}>
                  {testStatus.type === 'success' ? <CheckCircle className="h-4 w-4 shrink-0 text-emerald-600" /> : <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" />}
                  <span>{testStatus.msg}</span>
                </div>
              )}

              <div>
                <label className="text-caption uppercase font-extrabold text-slate-500 block mb-1">
                  Recipient Email Address <span className="text-rose-500">*</span>
                </label>
                <input
                  type="email"
                  value={testEmailAddress}
                  onChange={e => setTestEmailAddress(e.target.value)}
                  placeholder="e.g. admin@example.com"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 outline-none focus:bg-white focus:border-brand"
                  autoFocus
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowTestModal(false)}
                  className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSendTestEmail}
                  disabled={testSending}
                  className="flex-1 px-4 py-2 btn-primary text-white text-xs font-bold rounded-xl transition-all cursor-pointer disabled:opacity-50 flex items-center justify-center gap-1.5"
                >
                  {testSending ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  <span>{testSending ? 'Sending...' : 'Send Test'}</span>
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    );
  }

  // ── Template List View ──
  return (
    <div className="space-y-5 animate-fade-in">
      {/* List Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-indigo-600" />
            <h1 className="text-base font-extrabold text-slate-900">Email Templates</h1>
            <span className="text-xs text-slate-500 font-bold bg-slate-100 px-2 py-0.5 rounded-lg">
              {templates.length}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Create, edit, and maintain reusable automated email communication templates across workspace programs.
          </p>
        </div>

        <button onClick={startCreate} className="px-4 py-2.5 btn-primary text-white text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1.5 shadow-2xs shrink-0">
          <Plus className="h-4 w-4" />
          <span>New Email Template</span>
        </button>
      </div>

      {/* Search & Tag Filter Bar */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-xs p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-2.5 items-center">
          <div className="flex items-center bg-slate-100 p-1 rounded-xl shrink-0 text-xs font-bold border border-slate-200">
            <button
              onClick={() => setFilterScope('all')}
              className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${filterScope === 'all' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-500 hover:text-slate-800'}`}
            >
              All Templates
            </button>
            <button
              onClick={() => setFilterScope('mine')}
              className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${filterScope === 'mine' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-500 hover:text-slate-800'}`}
            >
              Created by Me
            </button>
            <button
              onClick={() => setFilterScope('shared')}
              className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${filterScope === 'shared' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-500 hover:text-slate-800'}`}
            >
              Shared with Team
            </button>
          </div>

          <div className="relative flex-1 w-full">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search templates by code, name, subject line, or tag..."
              className="w-full pl-10 pr-4 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand bg-slate-50 text-slate-900 placeholder:text-slate-400"
            />
          </div>

          <div className="flex items-center bg-slate-100 p-1 rounded-xl shrink-0">
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                viewMode === 'grid' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              <span>Grid</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                viewMode === 'table' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <TableIcon className="h-3.5 w-3.5" />
              <span>Table</span>
            </button>
          </div>
        </div>

        {allTags.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            <span className="text-[10px] uppercase font-extrabold text-slate-400 mr-1">Filter by Tag:</span>
            <button
              onClick={() => setFilterTag(null)}
              className={`px-2.5 py-1 text-caption font-bold rounded-lg transition-all cursor-pointer ${
                !filterTag ? 'bg-brand text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              All
            </button>
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => setFilterTag(filterTag === tag ? null : tag)}
                className={`px-2.5 py-1 text-caption font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1 ${
                  filterTag === tag ? 'bg-brand-primary text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Tag className="h-3 w-3" />
                <span>{tag}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Template Cards Grid */}
      {loading ? (
        <div className="py-20 text-center space-y-2">
          <RefreshCw className="animate-spin h-8 w-8 text-indigo-600 mx-auto" />
          <p className="text-xs text-slate-500 font-medium">Loading templates...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-2xl py-16 text-center space-y-3">
          <FileText className="h-12 w-12 text-slate-300 mx-auto" />
          <div>
            <p className="text-sm font-extrabold text-slate-700">{search || filterTag ? 'No matching templates found' : 'No email templates created yet'}</p>
            <p className="text-xs text-slate-500 mt-1 max-w-xs mx-auto">
              {search || filterTag ? 'Try adjusting your search criteria or tag filters.' : 'Click "New Email Template" to design your first automated email template.'}
            </p>
          </div>
          {!search && !filterTag && (
            <button onClick={startCreate} className="px-4 py-2 btn-primary text-white text-xs font-bold rounded-xl transition-all cursor-pointer">
              Create First Template
            </button>
          )}
        </div>
      ) : viewMode === 'table' ? (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-2xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider font-extrabold border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Template Name</th>
                  <th className="px-4 py-3">Subject Line</th>
                  <th className="px-4 py-3">Tags</th>
                  <th className="px-4 py-3">Version & Updated</th>
                  <th className="px-4 py-3">Sharing</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(t => (
                  <tr key={t.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 font-mono font-bold text-slate-600">{t.code || '—'}</td>
                    <td className="px-4 py-3 font-extrabold text-slate-900">{t.name}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{t.subject || '(No subject)'}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(t.tags || []).map(tag => (
                          <span key={tag} className="px-1.5 py-0.5 text-[10px] font-bold bg-indigo-50 text-indigo-700 rounded border border-indigo-100">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                      v{t.version} · {new Date(t.updatedAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-extrabold uppercase border ${
                        (t as any).isShared === false ? 'bg-amber-50 text-amber-800 border-amber-200' : isTemplateOwner(t) ? 'bg-indigo-50 text-indigo-800 border-indigo-200' : 'bg-slate-100 text-slate-700 border-slate-200'
                      }`}>
                        {(t as any).isShared === false ? 'Personal' : isTemplateOwner(t) ? 'Shared by You' : 'Shared'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => startEdit(t)} className="p-1.5 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors cursor-pointer" title="Edit template">
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button onClick={() => startDuplicate(t)} className="p-1.5 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors cursor-pointer" title="Duplicate template">
                          <Copy className="h-4 w-4" />
                        </button>
                        {canEdit && isTemplateOwner(t) && (
                          deleteConfirm === t.id ? (
                            <div className="flex items-center gap-1">
                              <button onClick={() => handleDelete(t.id)} className="px-2 py-1 bg-rose-600 text-white text-[10px] font-extrabold rounded cursor-pointer">Confirm</button>
                              <button onClick={() => setDeleteConfirm(null)} className="px-2 py-1 bg-slate-100 text-slate-600 text-[10px] font-extrabold rounded cursor-pointer">Cancel</button>
                            </div>
                          ) : (
                            <button onClick={() => setDeleteConfirm(t.id)} className="p-1.5 text-slate-600 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer" title="Delete template">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(t => (
            <div key={t.id} className="bg-white border border-slate-200 hover:border-indigo-300 rounded-2xl shadow-xs p-5 transition-all flex flex-col justify-between group">
              <div className="space-y-2.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h4 className="text-sm font-extrabold text-slate-900 group-hover:text-brand transition-colors truncate">
                      {t.code ? `[${t.code}] ` : ''}{t.name}
                    </h4>
                    <p className="text-caption text-slate-400 font-medium flex items-center gap-1.5 mt-0.5">
                      <span>Version {t.version}</span>
                      <span>·</span>
                      <span>{new Date(t.updatedAt).toLocaleDateString()}</span>
                    </p>
                  </div>
                  {t.hasAttachments && (
                    <span className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg shrink-0" title="Contains attachments">
                      <Paperclip className="h-3.5 w-3.5" />
                    </span>
                  )}
                </div>

                {/* Subject preview */}
                <div className="bg-slate-50 border border-slate-100 rounded-xl p-2.5">
                  <p className="text-[10px] uppercase font-extrabold text-slate-400">Subject Line</p>
                  <p className="text-xs font-medium text-slate-800 truncate mt-0.5">{t.subject || '(No subject)'}</p>
                </div>

                {/* Tags */}
                {(t.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {t.tags.slice(0, 3).map(tag => (
                      <span key={tag} className="px-2 py-0.5 text-[10px] font-bold bg-indigo-50 text-indigo-700 rounded-md border border-indigo-100">
                        {tag}
                      </span>
                    ))}
                    {t.tags.length > 3 && (
                      <span className="text-[11px] text-slate-500 font-bold self-center">+{t.tags.length - 3}</span>
                    )}
                  </div>
                )}
              </div>

              {/* Ownership & Sharing Badges */}
              <div className="pt-2">
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-extrabold uppercase tracking-wider border ${
                  (t as any).isShared === false
                    ? 'bg-amber-50 text-amber-800 border-amber-200'
                    : isTemplateOwner(t)
                    ? 'bg-indigo-50 text-indigo-800 border-indigo-200'
                    : 'bg-slate-100 text-slate-700 border-slate-200'
                }`}>
                  {(t as any).isShared === false
                    ? 'Personal (Private)'
                    : isTemplateOwner(t)
                    ? 'Shared by You'
                    : 'Shared with Team'}
                </span>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-4 mt-4 border-t border-slate-100">
                <button
                  onClick={() => startEdit(t)}
                  className="px-3 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-700 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1"
                >
                  <Edit3 className="h-3.5 w-3.5" />
                  <span>{canEdit && isTemplateOwner(t) ? 'Edit' : 'View / Use'}</span>
                </button>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => startDuplicate(t)}
                    className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all cursor-pointer"
                    title="Duplicate template"
                  >
                    <Copy className="h-4 w-4" />
                  </button>

                  {canEdit && isTemplateOwner(t) && (
                    deleteConfirm === t.id ? (
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleDelete(t.id)} className="px-2.5 py-1 bg-rose-600 text-white text-[10px] font-extrabold rounded-lg cursor-pointer hover:bg-rose-700 transition-all">Confirm</button>
                        <button onClick={() => setDeleteConfirm(null)} className="px-2.5 py-1 bg-slate-100 text-slate-600 text-[10px] font-extrabold rounded-lg cursor-pointer hover:bg-slate-200 transition-all">Cancel</button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(t.id)}
                        className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all cursor-pointer"
                        title="Delete template"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
